import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress, Box } from '@mui/material';
import { styled } from '@mui/material/styles';

const ProgressWrapper = styled(Box)({
  '&&': {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
});

const roundedCircleStyles = {
  strokeLinecap: 'round'
};

const BackgroundProgress = styled(CircularProgress)({
  '&&': {
    position: 'absolute',
    left: 0,
    top: 0,
    zIndex: 1
  },
  '& .MuiCircularProgress-circle': roundedCircleStyles
});

const ForegroundProgress = styled(CircularProgress)({
  '&&': {
    position: 'absolute',
    left: 0,
    top: 0,
    zIndex: 2,
    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
  },
  '& .MuiCircularProgress-circle': {
    ...roundedCircleStyles,
    transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
  }
});

const CenterContentBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'radius'
})(({ radius }) => ({
  '&&': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: radius * 2,
    height: radius * 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3,
    pointerEvents: 'none'
  }
}));

const CustomCircularProgress = ({
  radius,
  color,
  incompleteColor,
  borderWidth,
  value,
  variant,
  children,
  style,
  ...muiProps
}) => {
  const [animatedValue, setAnimatedValue] = useState(0);

  useEffect(() => {
    // Start animation from 0 to target value
    const timer = setTimeout(() => {
      setAnimatedValue(value);
    }, 100); // Small delay to ensure component is mounted

    return () => clearTimeout(timer);
  }, [value]);

  return (
    <ProgressWrapper
      style={{ width: radius * 2, height: radius * 2, ...style }}
      {...muiProps}
    >
      <BackgroundProgress
        variant="determinate"
        value={100}
        thickness={borderWidth}
        size={radius * 2}
        style={{ color: incompleteColor }}
      />

      <ForegroundProgress
        variant={variant}
        value={animatedValue}
        thickness={borderWidth}
        size={radius * 2}
        style={{ color }}
      />

      {children && (
        <CenterContentBox radius={radius}>
          {children}
        </CenterContentBox>
      )}
    </ProgressWrapper>
  );
};

CustomCircularProgress.propTypes = {
  radius: PropTypes.number,
  color: PropTypes.string,
  incompleteColor: PropTypes.string,
  borderWidth: PropTypes.number,
  value: PropTypes.number,
  variant: PropTypes.oneOf(['determinate', 'indeterminate']),
  children: PropTypes.node,
  style: PropTypes.object
};

CustomCircularProgress.defaultProps = {
  radius: 30,
  color: 'blue',
  incompleteColor: '#e0e0e0',
  borderWidth: 4,
  value: 75,
  variant: 'determinate',
  children: null,
  style: {}
};

export default CustomCircularProgress;
